import type { SaUser } from '@/mocks/saUsers';
import { saUsersActivityLog, saUsersModerationHistory, saUsersPaymentHistory } from '@/mocks/saUsers';
import { userTypeLabels, userTypeAccents, statusLabels, statusAccents, verificationLabels, verificationAccents } from './userLabels';

interface UserDetailModalProps {
  user: SaUser | null;
  onClose: () => void;
}

const tabs = [
  { id: 'profile', label: 'Perfil', icon: 'ri-user-line' },
  { id: 'activity', label: 'Actividad', icon: 'ri-history-line' },
  { id: 'moderation', label: 'Moderación', icon: 'ri-shield-check-line' },
  { id: 'payments', label: 'Pagos', icon: 'ri-money-dollar-circle-line' },
  { id: 'reports', label: 'Reportes', icon: 'ri-flag-line' },
] as const;

type TabId = typeof tabs[number]['id'];

import { useState } from 'react';

export default function UserDetailModal({ user, onClose }: UserDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('profile');

  if (!user) return null;

  const activity = saUsersActivityLog[user.id] || [];
  const moderation = saUsersModerationHistory[user.id] || [];
  const payments = saUsersPaymentHistory[user.id] || [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="fixed inset-0 z-[90] flex items-start justify-center pt-[5vh] pb-[5vh] px-4 overflow-y-auto">
        <div className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-foreground-950 flex items-center justify-center shrink-0">
                <span className="text-background-50 text-sm font-bold">{user.avatar}</span>
              </div>
              <div>
                <h3 className="text-foreground-950 text-sm font-bold">{user.name}</h3>
                <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold border ${userTypeAccents[user.userType]}`}>
                  {userTypeLabels[user.userType]}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-xl bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
            >
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 px-5 py-2 border-b border-background-200 bg-background-100/30 shrink-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-foreground-950 text-background-50'
                    : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
                {tab.id === 'activity' && activity.length > 0 && (
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${activeTab === tab.id ? 'bg-background-50/20 text-background-50' : 'bg-background-200 text-foreground-500'}`}>
                    {activity.length}
                  </span>
                )}
                {tab.id === 'moderation' && moderation.length > 0 && (
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${activeTab === tab.id ? 'bg-background-50/20 text-background-50' : 'bg-background-200 text-foreground-500'}`}>
                    {moderation.length}
                  </span>
                )}
                {tab.id === 'payments' && payments.length > 0 && (
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${activeTab === tab.id ? 'bg-background-50/20 text-background-50' : 'bg-background-200 text-foreground-500'}`}>
                    {payments.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {activeTab === 'profile' && (
              <>
                {/* Info grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-background-100/50 rounded-xl p-3">
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Email</p>
                    <p className="text-foreground-800 text-xs">{user.email}</p>
                  </div>
                  <div className="bg-background-100/50 rounded-xl p-3">
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Teléfono</p>
                    <p className="text-foreground-800 text-xs">{user.phone}</p>
                  </div>
                  <div className="bg-background-100/50 rounded-xl p-3">
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Rol Principal</p>
                    <p className="text-foreground-800 text-xs">{user.mainRole}</p>
                  </div>
                  <div className="bg-background-100/50 rounded-xl p-3">
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Radio Asociada</p>
                    <p className="text-foreground-800 text-xs">{user.associatedRadio}</p>
                  </div>
                  <div className="bg-background-100/50 rounded-xl p-3">
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">País / Ciudad</p>
                    <p className="text-foreground-800 text-xs">{user.country} — {user.city}</p>
                  </div>
                  <div className="bg-background-100/50 rounded-xl p-3">
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Se unió vía</p>
                    <p className="text-foreground-800 text-xs">{user.joinedVia}</p>
                  </div>
                </div>

                {/* Status badges */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${statusAccents[user.accountStatus]}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${user.accountStatus === 'activo' ? 'bg-green-500' : user.accountStatus === 'suspendido' ? 'bg-amber-500' : user.accountStatus === 'bloqueado' ? 'bg-red-500' : 'bg-blue-500'}`}></span>
                    {statusLabels[user.accountStatus]}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${verificationAccents[user.verificationStatus]}`}>
                    {user.verificationStatus === 'verificado' ? <i className="ri-verified-badge-fill text-[10px]"></i> : <i className="ri-error-warning-line text-[10px]"></i>}
                    {verificationLabels[user.verificationStatus]}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-[10px] font-semibold">
                    <i className={user.twoFactorEnabled ? 'ri-shield-check-line text-green-500' : 'ri-shield-line text-foreground-400'}></i>
                    {user.twoFactorEnabled ? '2FA Activado' : '2FA Desactivado'}
                  </span>
                  {user.activeSessions > 0 && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-[10px] font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      {user.activeSessions} sesión{user.activeSessions > 1 ? 'es' : ''} activa{user.activeSessions > 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Bio */}
                <div>
                  <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Bio</p>
                  <p className="text-foreground-700 text-xs leading-relaxed">{user.bio}</p>
                </div>

                {/* Quick info */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="bg-background-100/50 rounded-xl p-3 text-center">
                    <p className="text-foreground-400 text-[10px] mb-0.5">Registro</p>
                    <p className="text-foreground-800 text-xs font-semibold">{user.registrationDate}</p>
                  </div>
                  <div className="bg-background-100/50 rounded-xl p-3 text-center">
                    <p className="text-foreground-400 text-[10px] mb-0.5">Último Acceso</p>
                    <p className="text-foreground-800 text-xs font-semibold">{user.lastAccess}</p>
                  </div>
                  <div className="bg-background-100/50 rounded-xl p-3 text-center">
                    <p className="text-foreground-400 text-[10px] mb-0.5">Dispositivo</p>
                    <p className="text-foreground-800 text-xs font-semibold truncate">{user.deviceLastUsed}</p>
                  </div>
                  <div className="bg-background-100/50 rounded-xl p-3 text-center">
                    <p className="text-foreground-400 text-[10px] mb-0.5">Reportes</p>
                    <p className="text-foreground-800 text-xs font-semibold">{user.reportsCount}</p>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-2">
                {activity.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                      <i className="ri-history-line text-foreground-400 text-xl"></i>
                    </div>
                    <p className="text-foreground-500 text-sm">Sin actividad registrada</p>
                  </div>
                ) : (
                  activity.map((entry) => (
                    <div key={entry.id} className="flex items-start gap-3 p-3 rounded-xl bg-background-100/50">
                      <div className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-record-circle-line text-foreground-500 text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-foreground-800 text-xs font-semibold">{entry.action}</span>
                          <span className="px-1.5 py-0.5 rounded-md bg-background-200 text-foreground-500 text-[10px] font-medium">{entry.module}</span>
                        </div>
                        <p className="text-foreground-500 text-[11px] mt-0.5">{entry.details}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-foreground-400 text-[10px]">{entry.date}</span>
                          <span className="text-foreground-400 text-[10px]">· IP: {entry.ipAddress}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'moderation' && (
              <div className="space-y-2">
                {moderation.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                      <i className="ri-shield-check-line text-foreground-400 text-xl"></i>
                    </div>
                    <p className="text-foreground-500 text-sm">Sin historial de moderación</p>
                  </div>
                ) : (
                  moderation.map((entry) => (
                    <div key={entry.id} className="p-3 rounded-xl bg-background-100/50 space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <span className="text-foreground-800 text-xs font-semibold">{entry.reason}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          entry.status === 'activo' ? 'bg-red-50 text-red-700' : entry.status === 'resuelto' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                        }`}>
                          {entry.status === 'activo' ? 'Activo' : entry.status === 'resuelto' ? 'Resuelto' : 'Apelado'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-foreground-500 flex-wrap">
                        <span><i className="ri-calendar-line mr-1"></i>{entry.date}</span>
                        <span><i className="ri-user-settings-line mr-1"></i>{entry.moderator}</span>
                        <span><i className="ri-shield-line mr-1"></i>{entry.action}</span>
                      </div>
                      <p className="text-foreground-500 text-[11px]">{entry.notes}</p>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'payments' && (
              <div className="space-y-2">
                {payments.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                      <i className="ri-money-dollar-circle-line text-foreground-400 text-xl"></i>
                    </div>
                    <p className="text-foreground-500 text-sm">Sin pagos registrados</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-foreground-800 text-xs font-semibold">Total pagado: ${payments.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}</span>
                      <span className="text-foreground-500 text-[11px]">{payments.length} transacción{payments.length > 1 ? 'es' : ''}</span>
                    </div>
                    {payments.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between p-3 rounded-xl bg-background-100/50 flex-wrap gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                            entry.status === 'completado' ? 'bg-green-100 text-green-600' : entry.status === 'pendiente' ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-600'
                          }`}>
                            <i className={`text-sm ${entry.status === 'completado' ? 'ri-check-line' : entry.status === 'pendiente' ? 'ri-time-line' : 'ri-close-line'}`}></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-foreground-800 text-xs font-semibold truncate">{entry.concept}</p>
                            <div className="flex items-center gap-2 text-[10px] text-foreground-400">
                              <span>{entry.date}</span>
                              <span>· {entry.method}</span>
                              <span>· Ref: {entry.reference}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            entry.status === 'completado' ? 'bg-green-50 text-green-700' : entry.status === 'pendiente' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {entry.status === 'completado' ? 'Completado' : entry.status === 'pendiente' ? 'Pendiente' : 'Rechazado'}
                          </span>
                          <span className="text-foreground-800 text-xs font-bold whitespace-nowrap">${entry.amount.toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {activeTab === 'reports' && (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                  <i className="ri-flag-line text-foreground-400 text-xl"></i>
                </div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">{user.reportsCount} reporte{user.reportsCount !== 1 ? 's' : ''} recibido{user.reportsCount !== 1 ? 's' : ''}</p>
                <p className="text-foreground-400 text-xs">Los reportes detallados estarán disponibles al conectar con el sistema de moderación global.</p>
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-background-200 bg-background-100/30 shrink-0">
            {user.accountStatus === 'activo' && (
              <button
                onClick={onClose}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold cursor-pointer hover:bg-amber-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-pause-circle-line text-sm"></i>
                Suspender
              </button>
            )}
            {user.accountStatus === 'suspendido' && (
              <button
                onClick={onClose}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 border border-green-200 text-green-700 text-xs font-semibold cursor-pointer hover:bg-green-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-sm"></i>
                Reactivar
              </button>
            )}
            {user.accountStatus !== 'bloqueado' && (
              <button
                onClick={onClose}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-semibold cursor-pointer hover:bg-red-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-forbid-line text-sm"></i>
                Bloquear
              </button>
            )}
            {user.verificationStatus !== 'verificado' && (
              <button
                onClick={onClose}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold cursor-pointer hover:bg-blue-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-verified-badge-line text-sm"></i>
                Verificar
              </button>
            )}
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-foreground-950 text-background-50 text-xs font-semibold cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-key-2-line text-sm"></i>
              Restablecer Acceso
            </button>
          </div>
        </div>
      </div>
    </>
  );
}